import { AuthContext } from "@modules/auth/contexts/auth-plus.context";
import { useContext, useState, useEffect } from "react";
import {
  FaPlus,
  FaEdit,
  FaTrashAlt,
  FaEye,
  FaCogs,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";
import { FaSpinner as SpinnerIcon } from "react-icons/fa";
import { FeatureFlagService } from "../services/feature-flag.service";

interface FeatureFlag {
  _id?: string;
  featureId: string;
  description?: string;
  status: "ACTIVE" | "INACTIVE";
  rolloutPercentage: number;
  androidMinVersion: string;
  iosMinVersion: string;
  level: number;
}

type ModalMode = "CLOSED" | "ADD" | "VIEW" | "EDIT";

function FeatureManagement() {
  const { auth, setAuth, logout } = useContext(AuthContext);
  const [features, setFeatures] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Modal State
  const [modalMode, setModalMode] = useState<ModalMode>("CLOSED");
  const [selectedFeature, setSelectedFeature] = useState<FeatureFlag | null>(
    null,
  );

  // Form State (CamelCase)
  const [formData, setFormData] = useState<FeatureFlag>({
    featureId: "",
    description: "",
    status: "INACTIVE",
    rolloutPercentage: 0,
    androidMinVersion: "",
    iosMinVersion: "",
    level: 0,
  });
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    loadFeatures();
  }, []);

  const loadFeatures = async () => {
    setLoading(true);
    setError(null);

    if (!auth) {
      setError("Authentication required");
      setLoading(false);
      return;
    }

    try {
      const data = await FeatureFlagService.getAllFeatureFlags(
        auth,
        setAuth,
        logout,
      );

      setFeatures(
        data.data.map((item: any) => ({
          _id: item._id,
          featureId: item.feature_id,
          description: item.description,
          status: item.status,
          rolloutPercentage: item.rollout_percentage,
          androidMinVersion: item.android_min_version,
          iosMinVersion: item.ios_min_version,
          level: item.level,
        })),
      );
    } catch (err: any) {
      setError(err.message || "Failed to load feature flags");
    } finally {
      setLoading(false);
    }
  };

  // --- Handlers ---

  const openModal = (mode: ModalMode, feature?: FeatureFlag) => {
    setModalMode(mode);
    setFormError(null);
    if (mode === "ADD") {
      setFormData({
        featureId: "",
        description: "",
        status: "INACTIVE",
        rolloutPercentage: 0,
        androidMinVersion: "",
        iosMinVersion: "",
        level: 0,
      });
    } else if (feature && (mode === "VIEW" || mode === "EDIT")) {
      setSelectedFeature(feature);
      setFormData({ ...feature });
    }
  };

  const closeModal = () => {
    setModalMode("CLOSED");
    setSelectedFeature(null);
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "number" ? parseInt(value) || 0 : value,
    }));
    setFormError(null); 
  };

  const validateForm = (isEdit: boolean): boolean => {
    if (!isEdit) {
      if (
        !formData.featureId ||
        formData.featureId.length < 1 ||
        formData.featureId.length > 50
      ) {
        setFormError("Feature ID is required (1-50 characters).");
        return false;
      }
      const idRegex = /^[A-Z0-9_]+$/;
      if (!idRegex.test(formData.featureId)) {
        setFormError(
          "Feature ID must be UPPER_SNAKE_CASE (e.g., MY_NEW_FEATURE).",
        );
        return false;
      }
    }

    // Validate Required Versions
    if (!formData.androidMinVersion || formData.androidMinVersion.length > 50) {
      setFormError("Android Min Version is required (1-50 chars).");
      return false;
    }
    if (!formData.iosMinVersion || formData.iosMinVersion.length > 50) {
      setFormError("iOS Min Version is required (1-50 chars).");
      return false;
    }

    // Validate Description length if present
    if (formData.description && formData.description.length > 250) {
      setFormError("Description cannot exceed 250 characters.");
      return false;
    }

    // Validate Numerics
    if (formData.rolloutPercentage < 0 || formData.rolloutPercentage > 100) {
      setFormError("Rollout Percentage must be between 0 and 100.");
      return false;
    }
    if (formData.level < 0) {
      setFormError("Level must be 0 or greater.");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!validateForm(modalMode === "EDIT")) return;

    setLoading(true);
    try {
      let success = false;

      if (!auth) {
        setError("Authentication required");
        return;
      }

      if (modalMode === "ADD") {
        success = await FeatureFlagService.createFeatureFlag(
          auth,
          setAuth,
          logout,
          formData.featureId,
          formData.description ?? "",
          formData.status,
          formData.rolloutPercentage,
          formData.androidMinVersion,
          formData.iosMinVersion,
          formData.level,
        );
        if (success) setSuccessMsg("Feature flag created successfully!");
      } else if (modalMode === "EDIT" && selectedFeature) {
        success = await FeatureFlagService.updateFeatureFlag(
          auth,
          setAuth,
          logout,
          selectedFeature.featureId,
          formData.description ?? "",
          formData.status,
          formData.rolloutPercentage,
          formData.androidMinVersion,
          formData.iosMinVersion,
          formData.level,
        );
        if (success) setSuccessMsg("Feature flag updated successfully!");
      }

      if (success) {
        closeModal();
        loadFeatures();
        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMsg(null), 3000);
      } else {
        throw new Error("Operation failed");
      }
    } catch (err: any) {
      setFormError(err.message || "An error occurred while saving.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (feature: FeatureFlag) => {
    if (
      !window.confirm(
        `Are you sure you want to delete feature flag "${feature.featureId}"? This action cannot be undone.`,
      )
    ) {
      return;
    }

    setLoading(true);
    if (!auth) {
      setError("Authentication required");
      return;
    }

    try {
      const success = await FeatureFlagService.deleteFeatureFlag(
        auth,
        setAuth,
        logout,
        feature.featureId,
      );
      if (success) {
        setSuccessMsg("Feature flag deleted.");
        loadFeatures();
        setTimeout(() => setSuccessMsg(null), 3000);
      } else {
        throw new Error("Delete failed");
      }
    } catch (err: any) {
      setError(err.message || "Failed to delete feature flag");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-11">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div className="d-flex align-items-center gap-3">
                <FaCogs size={35} className="text-primary" />
                <div>
                  <h1 className="h3 mb-0 fw-bold text-dark">
                    Feature Flag Management
                  </h1>
                  <p className="text-muted small mb-0">
                    Manage rollout status and versions
                  </p>
                </div>
              </div>
              <button
                className="btn btn-primary d-flex align-items-center gap-2"
                onClick={() => openModal("ADD")}
              >
                <FaPlus /> Add New Feature
              </button>
            </div>

            {/* Global Alerts */}
            {error && (
              <div
                className="alert alert-danger alert-dismissible fade show"
                role="alert"
              >
                <strong>Error:</strong> {error}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setError(null)}
                ></button>
              </div>
            )}
            {successMsg && (
              <div
                className="alert alert-success alert-dismissible fade show"
                role="alert"
              >
                {successMsg}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setSuccessMsg(null)}
                ></button>
              </div>
            )}

            {/* Loading Overlay */}
            {loading && (
              <div
                className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                style={{
                  backgroundColor: "rgba(255,255,255,0.7)",
                  zIndex: 1050,
                }}
              >
                <SpinnerIcon className="text-primary fa-spin" size={40} />
              </div>
            )}

            {/* Features Table */}
            <div className="card shadow-sm border-0">
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="ps-4">Feature ID</th>
                        <th>Status</th>
                        <th>Rollout %</th>
                        <th>Level</th>
                        <th>Description</th>
                        <th>Min Versions (And / iOS)</th>
                        <th className="text-end pe-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {features.length === 0 && !loading ? (
                        <tr>
                          <td
                            colSpan={7}
                            className="text-center py-5 text-muted"
                          >
                            No feature flags found. Add one to get started.
                          </td>
                        </tr>
                      ) : (
                        features.map((feature) => (
                          <tr key={feature._id || feature.featureId}>
                            <td className="ps-4 fw-bold font-monospace">
                              {feature.featureId}
                            </td>
                            <td>
                              <span
                                className={`badge bg-${
                                  feature.status === "ACTIVE"
                                    ? "success"
                                    : "secondary"
                                }`}
                              >
                                {feature.status === "ACTIVE" ? (
                                  <FaCheckCircle className="me-1" />
                                ) : (
                                  <FaTimesCircle className="me-1" />
                                )}
                                {feature.status}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <div
                                  className="progress"
                                  style={{ width: "60px", height: "6px" }}
                                >
                                  <div
                                    className="progress-bar"
                                    role="progressbar"
                                    style={{
                                      width: `${feature.rolloutPercentage}%`,
                                      backgroundColor:
                                        feature.rolloutPercentage === 100
                                          ? "#198754"
                                          : "#0d6efd",
                                    }}
                                  ></div>
                                </div>
                                <span className="small">
                                  {feature.rolloutPercentage}%
                                </span>
                              </div>
                            </td>
                            <td>{feature.level}</td>
                            <td
                              className="text-truncate"
                              style={{ maxWidth: "200px" }}
                              title={feature.description}
                            >
                              {feature.description || (
                                <span className="text-muted fst-italic">
                                  No description
                                </span>
                              )}
                            </td>
                            <td className="small text-muted">
                              <div>A: {feature.androidMinVersion}</div>
                              <div>I: {feature.iosMinVersion}</div>
                            </td>
                            <td className="text-end pe-4">
                              <div className="btn-group" role="group">
                                <button
                                  className="btn btn-sm btn-outline-info"
                                  title="View Details"
                                  onClick={() => openModal("VIEW", feature)}
                                >
                                  <FaEye />
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  title="Update"
                                  onClick={() => openModal("EDIT", feature)}
                                >
                                  <FaEdit />
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  title="Delete"
                                  onClick={() => handleDelete(feature)}
                                >
                                  <FaTrashAlt />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Unified Modal */}
      {modalMode !== "CLOSED" && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">
                  {modalMode === "ADD" && "Add New Feature Flag"}
                  {modalMode === "VIEW" &&
                    `Feature Details: ${selectedFeature?.featureId}`}
                  {modalMode === "EDIT" &&
                    `Update Feature: ${selectedFeature?.featureId}`}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeModal}
                ></button>
              </div>

              <div className="modal-body">
                {formError && (
                  <div className="alert alert-danger py-2 small">
                    {formError}
                  </div>
                )}

                <form id="featureForm" onSubmit={handleSubmit}>
                  <div className="row g-3">
                    {/* ID Field */}
                    <div className="col-md-6">
                      <label className="form-label small fw-bold">
                        Feature ID
                      </label>
                      <input
                        type="text"
                        className="form-control font-monospace"
                        name="featureId"
                        value={formData.featureId}
                        onChange={handleInputChange}
                        disabled={modalMode !== "ADD"} // Only editable during creation
                        placeholder="E.G., NEW_PROFILE_LAYOUT"
                        required={modalMode === "ADD"}
                      />
                      <div className="form-text">
                        UPPER_SNAKE_CASE. Required 1-50 chars.
                      </div>
                    </div>

                    {/* Status Field */}
                    <div className="col-md-6">
                      <label className="form-label small fw-bold">Status</label>
                      <select
                        className="form-select"
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        disabled={modalMode === "VIEW"}
                      >
                        <option value="INACTIVE">INACTIVE</option>
                        <option value="ACTIVE">ACTIVE</option>
                      </select>
                    </div>

                    {/* Description */}
                    <div className="col-12">
                      <label className="form-label small fw-bold">
                        Description
                      </label>
                      <textarea
                        className="form-control"
                        name="description"
                        rows={2}
                        value={formData.description}
                        onChange={handleInputChange}
                        disabled={modalMode === "VIEW"}
                        placeholder="Brief description of this feature..."
                        maxLength={250}
                      ></textarea>
                      <div className="form-text text-end">
                        {(formData.description || "").length} / 250
                      </div>
                    </div>

                    <div className="col-12">
                      <hr className="my-2" />
                    </div>

                    {/* Version Constraints */}
                    <div className="col-md-6">
                      <label className="form-label small fw-bold">
                        Android Min Version
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="androidMinVersion"
                        value={formData.androidMinVersion}
                        onChange={handleInputChange}
                        disabled={modalMode === "VIEW"}
                        placeholder="1.0.0"
                        required
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label small fw-bold">
                        iOS Min Version
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="iosMinVersion"
                        value={formData.iosMinVersion}
                        onChange={handleInputChange}
                        disabled={modalMode === "VIEW"}
                        placeholder="1.0.0"
                        required
                      />
                    </div>

                    {/* Numeric Constraints */}
                    <div className="col-md-6">
                      <label className="form-label small fw-bold">
                        Rollout Percentage (0-100)
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        name="rolloutPercentage"
                        min="0"
                        max="100"
                        value={formData.rolloutPercentage}
                        onChange={handleInputChange}
                        disabled={modalMode === "VIEW"}
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label small fw-bold">
                        Level (Min 0)
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        name="level"
                        min="0"
                        value={formData.level}
                        onChange={handleInputChange}
                        disabled={modalMode === "VIEW"}
                      />
                    </div>
                  </div>
                </form>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeModal}
                >
                  {modalMode === "VIEW" ? "Close" : "Cancel"}
                </button>
                {modalMode !== "VIEW" && (
                  <button
                    type="submit"
                    form="featureForm"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? <SpinnerIcon className="fa-spin me-2" /> : ""}
                    {modalMode === "ADD" ? "Create Feature" : "Save Changes"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default FeatureManagement;
